import { useEffect, useRef, useState } from 'react';
import { Camera, Home, X } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { deliveryAddressText } from '../work/geo';

// Final-step delivery confirmation (mirrors iOS DeliveryConfirmationView):
// optional photo capture → parent performs proximity check + upload +
// status update. Errors thrown by onConfirm are shown inline.
export default function ConfirmDeliverySheet({ open, order, onClose, onConfirm }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setError(null);
      setBusy(false);
      setPreview((p) => {
        if (p) URL.revokeObjectURL(p);
        return null;
      });
    }
  }, [open]);

  function pick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview((p) => {
      if (p) URL.revokeObjectURL(p);
      return URL.createObjectURL(f);
    });
    setFile(f);
    setError(null);
    e.target.value = '';
  }

  function clearPhoto() {
    setPreview((p) => {
      if (p) URL.revokeObjectURL(p);
      return null;
    });
    setFile(null);
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(file);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not complete the delivery. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const num = order ? String(order.orderId || order.id).slice(0, 8) : '';

  return (
    <Sheet open={open} onClose={onClose} title="Delivery Confirmation" maxHeight="92vh">
      <div className="space-y-4 p-4 pt-1">
        <div className="flex flex-col items-center gap-2 text-center">
          <Home size={64} className="text-blue-500" />
          <p className="text-lg font-extrabold text-gray-900">Arrived at Customer</p>
          <p className="text-xs text-gray-500">
            You've arrived at the customer's location. Take a photo if needed and
            confirm delivery.
          </p>
        </div>

        <div className="space-y-1.5 rounded-2xl bg-gray-50 p-3 text-xs">
          <p>
            <span className="font-semibold text-gray-700">Order #:</span> {num}
          </p>
          <p>
            <span className="font-semibold text-gray-700">Customer:</span>{' '}
            {order?.customerName || '—'}
          </p>
          {deliveryAddressText(order) && (
            <p>
              <span className="font-semibold text-gray-700">Address:</span>{' '}
              {deliveryAddressText(order)}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-700">Delivery Photo (Optional)</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={pick}
          />
          {preview ? (
            <div className="relative">
              <img
                src={preview}
                alt="Delivery"
                className="h-48 w-full rounded-2xl object-cover"
              />
              <button
                onClick={clearPhoto}
                className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white"
                aria-label="Remove photo"
              >
                <X size={15} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => inputRef.current?.click()}
              className="flex h-36 w-full flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50 text-gray-400 active:bg-gray-100"
            >
              <Camera size={38} className="text-blue-400" />
              <span className="text-xs font-medium">Tap to take photo</span>
            </button>
          )}
        </div>

        {error && (
          <p className="rounded-xl bg-red-50 p-3 text-center text-xs font-semibold text-red-600">
            {error}
          </p>
        )}

        <button
          onClick={confirm}
          disabled={busy}
          className="w-full rounded-2xl bg-green-500 py-3.5 text-sm font-bold text-white shadow-sm disabled:opacity-60 active:scale-[0.98]"
        >
          {busy ? 'Confirming…' : 'Confirm Delivery'}
        </button>
      </div>
    </Sheet>
  );
}
